import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material'
import { useEffect, useMemo } from 'react';
import { validarCpfCnpj } from '../../../../../helpers/verificadores';
import { formatarCPFCNPJ, formatarTelefone } from '../../../../../helpers/masks';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { validarTelefone } from '../../../../../helpers/validarTelefone';

export default function DialogAddEditDadoTerceiro({ open, index, onClose }: DialogAddEditDadoTerceiroProps) {

    const { setValue: setMainFormValue, getValues: getMainFormValues } = useFormContext();

    const isEdit = typeof index === 'number';

    const { control, handleSubmit, reset } = useForm<ITerceiroForm>({
        defaultValues: {
            nome: '',
            cpf: '',
            telefone: '',
            tipo_relacionamento: 'Outro',
            compra_prod_receita: false,
        },
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const opcoesTipoRelacionamento = useMemo(() => [
        { value: "Sócio", label: "Sócio" },
        { value: "Procurador", label: "Procurador" },
        { value: "Cônjuge", label: "Cônjuge" },
        { value: "Familiar", label: "Familiar" },
        { value: "Empregado", label: "Empregado" },
        { value: "Outro", label: "Outro" },
    ], []);

    useEffect(() => {
        if (open && isEdit) {
            const terceiroAtual = getMainFormValues(`dados_cadastrais.terceiros.${index}`);
            if (terceiroAtual) {
                reset({
                    ...terceiroAtual,
                });
            }
        } else if (open && !isEdit) {
            reset({
                nome: '',
                cpf: '',
                telefone: '',
                tipo_relacionamento: 'Outro',
                compra_prod_receita: false,
            });
        }
    }, [open, isEdit, index, reset, getMainFormValues]);

    const handleSave = handleSubmit((data) => {
        try {
            if (isEdit) {
                setMainFormValue(`dados_cadastrais.terceiros.${index}`, data);
                enqueueSnackbar('Terceiro atualizado com sucesso!', { variant: 'success' });
            } else {
                const terceirosAtuais = getMainFormValues('dados_cadastrais.terceiros') || [];
                setMainFormValue('dados_cadastrais.terceiros', [...terceirosAtuais, data]);
                enqueueSnackbar('Terceiro adicionado com sucesso!', { variant: 'success' });
            }
            onClose();
        } catch (error) {
            enqueueSnackbar('Erro ao salvar terceiro', { variant: 'error' });
            console.error('Erro ao salvar terceiro:', error);
        }
    }, (errors) => {
        enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'error' });
    });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle><b>{isEdit ? 'Editar' : 'Adicionar'} Terceiro Autorizado a Comprar</b></DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.8 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name="nome"
                            control={control}
                            rules={{
                                required: 'O nome é obrigatório',
                                maxLength: { value: 100, message: 'Nome deve ter no máximo 100 caracteres' }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    required
                                    label="Nome"
                                    placeholder="Nome*"
                                    fullWidth
                                    error={invalid}
                                    helperText={error?.message || ""}
                                    slotProps={{ htmlInput: { maxLength: 100 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name="cpf"
                            control={control}
                            rules={{
                                required: 'O CPF é obrigatório',
                                validate: (value) => {
                                    if (!value || value.trim() === "") return "O CPF é obrigatório";
                                    return validarCpfCnpj(value) || 'CPF inválido';
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    required
                                    label="CPF"
                                    placeholder="CPF"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                    error={invalid}
                                    helperText={error?.message || ""}
                                    slotProps={{ htmlInput: { maxLength: 20 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name="telefone"
                            control={control}
                            rules={{
                                required: 'O telefone é obrigatório',
                                maxLength: { value: 20, message: 'Telefone deve ter no máximo 20 caracteres' },
                                validate: (value) => {
                                    const telefoneValido = validarTelefone(value);
                                    return telefoneValido || "Telefone inválido";
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    label="Telefone"
                                    required
                                    placeholder="Telefone"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                                    error={invalid}
                                    helperText={error?.message || ""}
                                    slotProps={{ htmlInput: { maxLength: 20 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Controller
                            name="tipo_relacionamento"
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl fullWidth required error={invalid}>
                                    <InputLabel id="tipo-relacionamento-select">Função/relação</InputLabel>
                                    <Select
                                        {...field}
                                        labelId="tipo-relacionamento-select"
                                        label="Função/relação"
                                    >
                                        {opcoesTipoRelacionamento.map((opcao, index) => (
                                            <MenuItem key={index} value={opcao.value}>{opcao.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name="compra_prod_receita"
                            control={control}
                            render={({ field }) => (
                                <FormControl>
                                    <FormLabel id="compra-prod-receita">Compra produtos com receita?</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="compra-prod-receita"
                                        value={field.value ? 'true' : 'false'}
                                        onChange={(e) => field.onChange(e.target.value === 'true')}
                                    >
                                        <FormControlLabel value="true" control={<Radio />} label="Sim" />
                                        <FormControlLabel value="false" control={<Radio />} label="Não" />
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogAddEditDadoTerceiroProps {
    open: boolean;
    index?: number;
    onClose: () => void;
}

export interface ITerceiroForm {
    nome: string;
    cpf: string;
    telefone: string;
    tipo_relacionamento: 'Sócio' | 'Procurador' | 'Cônjuge' | 'Familiar' | 'Empregado' | 'Outro';
    compra_prod_receita: boolean;
}